import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import HeroSection from '@/components/home/HeroSection';
import SectionDivider from '@/components/home/SectionDivider';
import DailyFade from '@/components/home/DailyFade';
import FactModal from '@/components/shared/FactModal';
import ImageLightbox from '@/components/shared/ImageLightbox';
import { imagePathFor } from '@/lib/data/factImages';
import { getHomeChild, preloadHomeChildren } from '@/lib/homePreload';

// Renders one of Home's preloaded sections. NOT React.lazy()+Suspense:
// that always suspended on hydration's first render even after preloading
// (see homePreload.js for why) - this instead reads a synchronous cache
// that's already populated by the time hydration runs for a fresh page
// load. The only case where the cache can still be empty on first render is
// client-side navigation TO "/" from another page (no hydration involved,
// so no mismatch risk) - preloadHomeChildren() is memoized, so calling it
// again here just resolves once the modules land and re-renders normally.
function HomeChild({ name, ...props }) {
  const [, setTick] = useState(0);
  const Comp = getHomeChild(name);

  useEffect(() => {
    if (Comp) return;
    preloadHomeChildren().then(() => setTick((n) => n + 1));
  }, [Comp]);

  if (!Comp) return null;
  return <Comp {...props} />;
}

export default function Home() {
  const [selectedFact, setSelectedFact] = useState(null);
  const [imageFact, setImageFact] = useState(null);

  return (
    // A div, not <main>: AppLayout already wraps every route in the page's one
    // <main> landmark, and main-inside-main is invalid nesting.
    <div>
      {/* SEO: Meta Tags */}
      <Helmet>
        <title>Beastly Facts | Animal Facts and Pet Care Guides</title>
        <meta name="description" content="100+ species care guides and 400+ deep dives on setup, diet, health, handling, cost, and the law. Checked animal facts for keepers and the curious." />
        <link rel="canonical" href="https://beastlyfacts.com/" />
        <meta property="og:title" content="Beastly Facts | Animal Facts and Pet Care Guides" />
        <meta property="og:description" content="100+ species care guides and 400+ deep dives on setup, diet, health, handling, cost, and the law." />
        <meta property="og:url" content="https://beastlyfacts.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://beastlyfacts.com/assets/og-default.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Beastly Facts - animal facts and pet care guides" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Beastly Facts | Animal Facts and Pet Care Guides" />
        <meta name="twitter:description" content="100+ species care guides and 400+ deep dives on setup, diet, health, handling, cost, and the law." />
        <meta name="twitter:image" content="https://beastlyfacts.com/assets/og-default.jpg" />
      </Helmet>

      
      <HeroSection onOpenFact={setSelectedFact} />

      <DailyFade><HomeChild name="FeaturedEvent" /></DailyFade>

      <DailyFade><HomeChild name="TrendingFacts" onOpenFact={setSelectedFact} onOpenImage={setImageFact} /></DailyFade>
      {/* Directly under the fact cards: the strip continues the same rotation
          they use, and gives /gallery/ a real entry point. It had exactly one
          inbound link site-wide, on the Facts page. */}
      <DailyFade><HomeChild name="FactPhotoStrip" onOpenFact={setSelectedFact} /></DailyFade>
      <HomeChild name="FactsToGuidesBanner" />
      <HomeChild name="BeastleTeaser" />

      {/* Two dividers, marking the three blocks the eleven sections already were
          in intent: facts, reference, editorial. A tinted band behind each block
          was tried here and taken back out - see SectionDivider.jsx. */}
      <SectionDivider />

      <DailyFade><HomeChild name="CategoryBrowse" /></DailyFade>
      {/* Reference content: animal profiles + care guides, together.
          Beastlypedia leads it because the wild-animal thread runs unbroken
          from TrendingFacts through CategoryBrowse into here, and the fact
          database is mostly wild animals. The handover to pet care happens
          once, at EncyclopediaTeaser, instead of twice. */}
      <DailyFade><HomeChild name="BeastlypediaTeaser" /></DailyFade>
      <DailyFade><HomeChild name="EncyclopediaTeaser" /></DailyFade>
      <DailyFade><HomeChild name="GuideSpotlight" /></DailyFade>

      <SectionDivider />

      {/* Editorial content: articles + fiction + play, together */}
      <DailyFade><HomeChild name="CritterDigestPreview" /></DailyFade>
      <HomeChild name="DexTeaser" />
      <HomeChild name="QuizzesTeaser" />
      <HomeChild name="Newsletter" />
      <FactModal fact={selectedFact} onClose={() => setSelectedFact(null)} onOpenImage={setImageFact} />
      <ImageLightbox fact={imageFact} imagePath={imagePathFor(imageFact)} onClose={() => setImageFact(null)} />
    </div>
  );
}
